import React, { useState, useEffect } from 'react'
import styled from '@emotion/styled'
import { Button } from '@mobi/component-library/Base/Buttons'
import { Spinner } from '@mobi/component-library/Legacy/Spinner'
import { VerificationCodeInput } from '@mobi/component-library/Selection&Input/Inputs/VerificationCodeInput'
import { Icon } from '@mobi/component-library/Assets/Icon'
import { colors, font, radius, spacing } from '@mobi/component-library/Foundations'
import { calculateTimeLeft, formatTime } from '../Helpers/utils'
import { MfaCodePageHeader } from '../Pages/VerificationCodePage.styles'
import { useFeature } from '@core/Utils/hooks'
import { FeatureFlags } from '@mobi/settings/featureFlags'
import { isCodeMismatchError, isRateLimitError, trackMfaEvent } from '../Helpers/helpers'
import { MfaAnalyticsData } from '@classic/Foundation/Analytics/AnalyticsDataLayer'

interface VerificationCodeFormProps {
  /**
   * Must be an async operation that either succeeds or throws a specific error type.
   * Loading state is managed internally by this component.
   * @param code - The 6-digit verification code to verify.
   * @throws {VerificationStandardError} When verification fails. Constructor accepts error message.
   * Error message is used to set error state internally.
   */
  onVerify: (code: string) => Promise<void>

  /**
   * Requests a new verification code to be sent.
   * Must be an async operation that either succeeds or throws a specific error type.
   * Loading state is managed internally by this component.
   * @throws {Error} When resend operation fails. Constructor accepts error message.
   * Error message is used to set error state internally.
   */
  onResend: () => Promise<void>

  onEditContact?: (error: string, expiryTime: number) => void

  shouldShowHeader?: boolean
  title?: string
  children?: React.ReactNode

  // these should only be used for GA tracking
  analyticsFlowName?: MfaAnalyticsData['otpFlow']
  analyticsMethodName?: MfaAnalyticsData['otpMethod']
}

export const VerificationCodeForm: React.FC<VerificationCodeFormProps> = ({
  onVerify,
  onResend,
  onEditContact,

  shouldShowHeader = true,
  title,
  children,

  analyticsFlowName,
  analyticsMethodName,
}) => {
  const [code, setCode] = useState('')
  const [error, setError] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const isOtpContactDetailsEditEnabled = useFeature(FeatureFlags.OTP_CONTACT_DETAILS_EDIT.key)
  const [expiryTime, setExpiryTime] = useState(Date.now() + 3 * 60 * 1000)
  const [timeLeft, setTimeLeft] = useState(calculateTimeLeft(expiryTime))
  const isDisabled = isRateLimitError(error)

  const isExpired = timeLeft <= 0
  const isError = !!error

  const defaultAnalyticsProperties = {
    otpFlow: analyticsFlowName ?? 'Unknown',
    otpMethod: analyticsMethodName ?? 'Unknown',
  }

  useEffect(() => {
    const timer = setInterval(() => {
      const newTimeLeft = calculateTimeLeft(expiryTime)

      if (newTimeLeft <= 0) {
        clearInterval(timer)
      }
      setTimeLeft(newTimeLeft)
    }, 1000)

    return () => clearInterval(timer)
  }, [expiryTime])

  useEffect(() => {
    if (isDisabled == true) {
      resetVerificationState(Date.now())
    } else {
      if (!error) {
        resetVerificationState()
      }
    }
  }, [isDisabled])

  const resetVerificationState = (newExpiryTime?: number) => {
    setCode('')
    setExpiryTime(newExpiryTime || Date.now() + 3 * 60 * 1000)
  }

  const handleCodeChange = (newCode: string) => {
    if (!isDisabled) {
      setError('')
      setCode(newCode)
    }
  }

  const handleVerifyClick = async (newCode?: string) => {
    setIsLoading(true)
    const submitCode = newCode ? newCode : code

    const filledDigits = submitCode.length
    if (filledDigits < 6) {
      setError('Please enter a valid verification code')
      setIsLoading(false)
      return
    }

    try {
      await onVerify(submitCode)
      trackMfaEvent({
        ...defaultAnalyticsProperties,
        interaction_type: 'OTP Submit',
      })
    } catch (error) {
      const genericError = 'Sorry, there was an error verifying the code'
      if (error instanceof Error) {
        const errorMessage = error.message ?? genericError
        if (isRateLimitError(errorMessage) || isCodeMismatchError(errorMessage)) {
          setError(errorMessage)
        } else {
          setError(genericError)
        }
      } else {
        setError(genericError)
      }

      trackMfaEvent({
        ...defaultAnalyticsProperties,
        interaction_type: 'OTP Error',
        message: 'Incorrect Code',
      })
    } finally {
      setIsLoading(false)
    }
  }

  const handleResendClick = async () => {
    setIsLoading(true)
    setError('')
    try {
      await onResend()

      trackMfaEvent({
        ...defaultAnalyticsProperties,
        interaction_type: 'OTP resend',
      })
    } catch (error) {
      const genericError = 'Sorry, there was an error resending the code'
      if (error instanceof Error) {
        const errorMessage = error.message ?? genericError
        if (isRateLimitError(errorMessage) || isCodeMismatchError(errorMessage)) {
          setError(errorMessage)
        } else {
          setError(genericError)
        }
      } else {
        setError(genericError)
      }

      trackMfaEvent({
        ...defaultAnalyticsProperties,
        interaction_type: 'OTP Error',
        message: 'Resend Failed',
      })
    } finally {
      setIsLoading(false)
      if (!isDisabled) {
        resetVerificationState()
      }
    }
  }

  const isVerifyDisabled =
    [...code].filter(x => !isNaN(parseInt(x))).length < 6 || isError || isDisabled || isLoading
  const isInputDisabled = isDisabled || isExpired || isLoading

  return (
    <MfaFormContainer data-tid-mfa-code-verification-form>
      {shouldShowHeader || title ? (
        <MfaCodePageHeader>{title ?? 'Verify your Identity'}</MfaCodePageHeader>
      ) : null}
      {children}
      <VerificationCodeInput
        code={code}
        onChange={handleCodeChange}
        onSubmit={handleVerifyClick}
        isValid={!isError && !isExpired}
        isDisabled={isInputDisabled}
      />

      {isError ? (
        <MfaFormMessageContainer>
          <Icon name='LineAlertCircle' size='1.4rem' color={colors.error[600]} />
          <MfaFormErrorMessage>{error}</MfaFormErrorMessage>
        </MfaFormMessageContainer>
      ) : null}

      {!isExpired && !isError && (
        <MfaCodeFormText>
          Your code will expire in <b>{formatTime(timeLeft)}</b> mins.
        </MfaCodeFormText>
      )}

      {isExpired && !isDisabled && (
        <MfaCodeFormText style={{ color: colors.error[600] }}>
          Your code has expired. Please request a new one.
        </MfaCodeFormText>
      )}

      {isExpired || !!isError ? (
        <>
          <MfaPrimaryButton onClick={handleResendClick} color='primary'>
            Request new code
          </MfaPrimaryButton>
          {isOtpContactDetailsEditEnabled && onEditContact ? (
            <MfaSecondaryButton
              onClick={() => onEditContact(error, expiryTime)}
              color='secondary_color'
            >
              <MfaButtonPrimarySpan style={{ ...font.size.lg }}>
                Edit contact details
              </MfaButtonPrimarySpan>
            </MfaSecondaryButton>
          ) : null}
        </>
      ) : (
        <>
          <MfaPrimaryButton
            onClick={() => handleVerifyClick()}
            color='primary'
            disabled={isLoading || isVerifyDisabled}
          >
            Verify
          </MfaPrimaryButton>
          <MfaSecondaryButton
            onClick={handleResendClick}
            disabled={isLoading}
            color='secondary_color'
          >
            <MfaButtonPrimarySpan style={{ ...font.size.sm }}>
              Didn't receive a code?
            </MfaButtonPrimarySpan>
            <MfaButtonSecondarySpan> Resend</MfaButtonSecondarySpan>
          </MfaSecondaryButton>
        </>
      )}

      {isLoading ? <Spinner /> : null}
    </MfaFormContainer>
  )
}

// ======
// Styles
// ======

const MfaFormContainer = styled.div({
  display: 'flex',
  flexDirection: 'column',
  padding: '16px',
  gap: '16px',
})

const MfaCodeFormText = styled.p({
  textAlign: 'left',
})

const MfaFormMessageContainer = styled.div({
  display: 'flex',
  alignItems: 'center',
  gap: '0.5rem',
  minHeight: '21px',
})

const MfaFormErrorMessage = styled.p({
  color: colors.error[600],
  ...font.size.md,
  margin: '0',
})

const MfaPrimaryButton = styled(Button)({})

const MfaSecondaryButton = styled(Button)({
  display: 'flex',
  justifyContent: 'center',
  gap: '0.75rem',
  alignItems: 'center',
  fontFamily: 'inherit',
  border: 'none',
  backgroundColor: colors.neutral[100],
  padding: `${spacing.smx1} ${spacing.sm}`,
  borderRadius: radius.sm,
})

const MfaButtonPrimarySpan = styled.span({
  fontWeight: font.weight.regular,
  color: colors.neutral[800],
})

const MfaButtonSecondarySpan = styled.span({
  fontWeight: font.weight.semibold,
  fontSize: '14px',
  color: colors.studio[500],
  padding: `${spacing.smx1} 0`,
})
